import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  buildDefaultPhotoCropPoints,
  buildPhotoCaptureCropLayout,
} from "./photoCaptureCropUtils";

let photoCaptureMaskCounter = 0;

const DEFAULT_HEADER_OFFSET = 50;
const DEFAULT_BOTTOM_NAV_OFFSET = 76;
const DEFAULT_CHROME_OFFSET = 72;

const readCssLengthPx = (styles, property, fallback) => {
  const raw = styles.getPropertyValue(property).trim();
  if (!raw || raw.startsWith("calc")) {
    return fallback;
  }
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const readSafeAreaInsetTop = () => {
  if (typeof document === "undefined") {
    return 0;
  }
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:absolute;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top, 0px)";
  document.body.appendChild(probe);
  const value = parseFloat(getComputedStyle(probe).paddingTop) || 0;
  document.body.removeChild(probe);
  return value;
};

const getPreviewMaxHeight = () => {
  const styles = getComputedStyle(document.documentElement);
  const header = readCssLengthPx(
    styles,
    "--photo-capture-header-offset",
    DEFAULT_HEADER_OFFSET,
  );
  const bottomNav = readCssLengthPx(
    styles,
    "--photo-capture-bottom-nav-offset",
    DEFAULT_BOTTOM_NAV_OFFSET,
  );
  const chrome = readCssLengthPx(
    styles,
    "--photo-capture-preview-chrome-offset",
    DEFAULT_CHROME_OFFSET,
  );
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  return viewportHeight - header - readSafeAreaInsetTop() - bottomNav - chrome;
};

const PhotoCaptureCropEditor = ({
  imageUrl,
  draftPoints,
  onDraftPointsChange,
  disabled,
}) => {
  const containerRef = useRef(null);
  const viewportRef = useRef(null);
  const maskIdRef = useRef(null);
  if (!maskIdRef.current) {
    photoCaptureMaskCounter += 1;
    maskIdRef.current = `photo-capture-crop-mask-${photoCaptureMaskCounter}`;
  }
  const maskId = maskIdRef.current;
  const dragPointIndexRef = useRef(null);
  const [layout, setLayout] = useState(null);

  const buildLayout = useCallback((image) => {
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;
    if (!naturalWidth || !naturalHeight) {
      return null;
    }

    const containerWidth = containerRef.current?.clientWidth || naturalWidth;
    return buildPhotoCaptureCropLayout(
      naturalWidth,
      naturalHeight,
      containerWidth,
      getPreviewMaxHeight(),
    );
  }, []);

  const syncLayoutFromImage = useCallback(
    (image) => {
      const nextLayout = buildLayout(image);
      if (!nextLayout) {
        return;
      }
      setLayout(nextLayout);
      if (draftPoints.length === 0) {
        onDraftPointsChange(
          buildDefaultPhotoCropPoints(
            nextLayout.naturalWidth,
            nextLayout.naturalHeight,
          ),
        );
      }
    },
    [buildLayout, draftPoints.length, onDraftPointsChange],
  );

  const handleImageLoad = useCallback(
    (event) => {
      syncLayoutFromImage(event.currentTarget);
    },
    [syncLayoutFromImage],
  );

  useLayoutEffect(() => {
    const host = containerRef.current;
    if (!host) {
      return undefined;
    }

    const refresh = () => {
      const image = host.querySelector("img");
      if (!image?.complete || !image.naturalWidth) {
        return;
      }
      syncLayoutFromImage(image);
    };

    refresh();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(refresh)
        : null;
    resizeObserver?.observe(host);

    const onViewportResize = () => {
      refresh();
    };
    window.visualViewport?.addEventListener("resize", onViewportResize);
    window.addEventListener("resize", onViewportResize);

    return () => {
      resizeObserver?.disconnect();
      window.visualViewport?.removeEventListener("resize", onViewportResize);
      window.removeEventListener("resize", onViewportResize);
    };
  }, [imageUrl, syncLayoutFromImage]);

  const pointerToImagePoint = useCallback(
    (clientX, clientY) => {
      if (!layout) {
        return null;
      }
      const rect =
        viewportRef.current?.getBoundingClientRect() ??
        containerRef.current?.getBoundingClientRect();
      if (!rect) {
        return null;
      }
      const x = (clientX - rect.left) / layout.scale;
      const y = (clientY - rect.top) / layout.scale;
      const clampedX = Math.max(
        0,
        Math.min(layout.naturalWidth, Math.round(x)),
      );
      const clampedY = Math.max(
        0,
        Math.min(layout.naturalHeight, Math.round(y)),
      );
      return [clampedX, clampedY];
    },
    [layout],
  );

  const moveDraggedPoint = useCallback(
    (clientX, clientY) => {
      const pointIndex = dragPointIndexRef.current;
      if (pointIndex === null || disabled) {
        return;
      }
      const point = pointerToImagePoint(clientX, clientY);
      if (!point) {
        return;
      }
      onDraftPointsChange((currentPoints) =>
        currentPoints.map((existingPoint, index) =>
          index === pointIndex ? point : existingPoint,
        ),
      );
    },
    [disabled, onDraftPointsChange, pointerToImagePoint],
  );

  const handlePointPointerDown = useCallback(
    (event, pointIndex) => {
      if (disabled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      dragPointIndexRef.current = pointIndex;
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [disabled],
  );

  const handlePointPointerMove = useCallback(
    (event) => {
      if (dragPointIndexRef.current === null) {
        return;
      }
      event.preventDefault();
      moveDraggedPoint(event.clientX, event.clientY);
    },
    [moveDraggedPoint],
  );

  const endPointDrag = useCallback((event) => {
    dragPointIndexRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const stagePoints = useMemo(() => {
    if (!layout) {
      return "";
    }
    return draftPoints
      .map(([x, y]) => `${x * layout.scale},${y * layout.scale}`)
      .join(" ");
  }, [draftPoints, layout]);

  if (!imageUrl) {
    return null;
  }

  const viewportStyle = layout
    ? {
        width: layout.displayWidth,
        height: layout.displayHeight,
      }
    : {
        width: "100%",
        minHeight: 120,
      };

  return (
    <div className="photo-capture-crop-editor-host" ref={containerRef}>
      <div
        className="photo-capture-crop-viewport"
        ref={viewportRef}
        style={viewportStyle}
      >
        <img
          src={imageUrl}
          alt=""
          className="photo-capture-crop-image"
          onLoad={handleImageLoad}
          draggable={false}
          style={
            layout
              ? {
                  width: layout.displayWidth,
                  height: layout.displayHeight,
                }
              : {
                  display: "block",
                  width: "100%",
                  height: "auto",
                  maxHeight: `calc(100dvh - var(--photo-capture-header-offset) - var(--photo-capture-bottom-nav-offset) - var(--photo-capture-preview-chrome-offset, 72px))`,
                  objectFit: "contain",
                }
          }
        />
        {layout && (
          <svg
            className="photo-capture-crop-overlay"
            width={layout.displayWidth}
            height={layout.displayHeight}
          >
            {draftPoints.length >= 3 && (
              <>
                <defs>
                  <mask id={maskId}>
                    <rect
                      width={layout.displayWidth}
                      height={layout.displayHeight}
                      fill="white"
                    />
                    <polygon points={stagePoints} fill="black" />
                  </mask>
                </defs>
                <rect
                  width={layout.displayWidth}
                  height={layout.displayHeight}
                  fill="rgba(15, 38, 92, 0.52)"
                  mask={`url(#${maskId})`}
                />
              </>
            )}
            {draftPoints.length >= 2 && (
              <polygon
                points={stagePoints}
                fill="rgba(0, 167, 166, 0.15)"
                stroke="#00a7a6"
                strokeWidth="2"
              />
            )}
            {draftPoints.map(([x, y], index) => (
              <g key={`crop-point-${index}`}>
                <circle
                  className="photo-capture-crop-point-hit"
                  cx={x * layout.scale}
                  cy={y * layout.scale}
                  r="22"
                  fill="transparent"
                  onPointerDown={(event) =>
                    handlePointPointerDown(event, index)
                  }
                  onPointerMove={handlePointPointerMove}
                  onPointerUp={endPointDrag}
                  onPointerCancel={endPointDrag}
                />
                <circle
                  className="photo-capture-crop-point"
                  cx={x * layout.scale}
                  cy={y * layout.scale}
                  r="12"
                  fill="#00a7a6"
                  stroke="#ffffff"
                  strokeWidth="2"
                  pointerEvents="none"
                />
              </g>
            ))}
          </svg>
        )}
      </div>
    </div>
  );
};

export default PhotoCaptureCropEditor;
